import type { Option } from "@tento-lona/sheets";

/**
 * Legend width computation for DefaultRow legends.
 *
 * ## Layout
 *
 *   ┌──────────┬──────────────────────────────┬─────────────┐
 *   │ row-icon │ #label-container (flex-grow) │ badge (20)  │
 *   │  24px    │  marginL:1  padL:8  padR:8   │ visibility:  │
 *   │          │  #label .small (text)        │  hidden     │
 *   └──────────┴──────────────────────────────┴─────────────┘
 *   #root: padding-right: 8px
 *   SheetLegend #root: padding-left: 8px
 *
 * ## Constants (from sentinel bounding boxes)
 *
 *   icon          = 24px   (std-row-icon fixed)
 *   lcMarginL     =  1px   (#label-container margin-left)
 *   lcPadL        =  8px   (#label-container padding-left)
 *   lcPadR        =  8px   (#label-container padding-right)
 *   rootPadR      =  8px   (#root padding-right)
 *   badge         = 20px   (Avatar, always in DOM, visibility:hidden)
 *   badgePadL     =  1px   (#timescale-indicator padding-left)
 *   SheetLegendPad=  8px   (SheetLegend #root padding-left)
 *                 ──────
 *   OVERHEAD      = 78px
 *
 * ## Verified from
 *
 *   sentinel label="Untitled" (default-row/layout):
 *     host=112  icon=24  labelContainer=58  labelEl=42  badgeW=20  badgePadL=1
 *     host = 24 + 1 + 8 + 42 + 8 + 8 + 20 + 1 = 112 ✓
 *     body = 112 + 8 (SheetLegend pad) = 120
 *     formula = ceil(42) + 78 = 120 ✓
 */

export interface TextMeasurer {
  /** Returns the pixel width of the given text. Uses the default font
   *  unless a CSS font string is provided. */
  measureText(text: string, font: Option<string>): number;
  /** Font strings by element ID (e.g., "label", "tzname", "title"). */
  readonly fonts: Readonly<Record<string, string>>;
}

/**
 * One row as the legend width is measured: its label and whether its
 * timescale shows.
 */
export interface DefaultLegendLayoutInput {
  label: string;
  timescaleVisible: boolean;
}

/**
 * Compute the natural width of a DefaultRow legend in pixels.
 *
 * OVERHEAD already includes the badge (20px) since it's always in
 * the DOM. Callers should pass `timescaleVisible: false`.
 */
function defaultLegendWidth(
  input: DefaultLegendLayoutInput,
  measure: TextMeasurer,
): number {
  const textWidth = measure.measureText(input.label, null);
  const timescale = input.timescaleVisible ? BADGE_WIDTH : 0;
  return Math.ceil(textWidth) + OVERHEAD + timescale;
}

const OVERHEAD = 78;
const BADGE_WIDTH = 20;

/** Measures the default width of a sheet's legend. */
export namespace DefaultLegendLayout {
  export const width = defaultLegendWidth;
}
